import React, { useState, lazy } from "react";
import clsx from "clsx";
import { isMobileOnly } from "react-device-detect";
import CloseIcon from "@material-ui/icons/Close";
import R from "ramda";
import {
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  Slide,
} from "@material-ui/core";

import { FACE_PAGE_FLOWS } from "enums";
import { useFaces, useRouter, useSettings, useUsers } from "hooks";
import { appRoutes } from "routes";
import { allToBase64, rotateAndScale } from "utils/imageUtils";
import browsePic from "assets/images/face-upload/browse.png";
import webcamPic from "assets/images/face-upload/webcam.png";
import facebookPic from "assets/images/face-upload/facebook.png";
import { relationToPronoun } from "utils/relativeUtils";
import { trackEventHeadUploadSuccess } from "utils/gAnalyticsUtils";

import Webcam from "./components/Webcam";
import useStyles from "./useStyles";

const ReactFilestack = lazy(() => import("filestack-react"));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FaceUploadModal = () => {
  const classes = useStyles();
  const [isWebcamVisible, setIsWebcamVisible] = useState(false);
  const {
    settings: { fileStack },
  } = useSettings();
  const { pushWithSave } = useRouter();
  const { currentUser } = useUsers();

  const {
    photo,
    setPhoto,
    closeModal,
    clearPhoto,
    clearFoundFaces,
    resetAdded,
    setMode,
    mode,
    relationToCreate,
    clearRelationToCreate,
    clearFacesToAddPerson,
  } = useFaces();
  const goToFace = () => pushWithSave(appRoutes.facePath());

  const cleanData = () => {
    clearPhoto();
    clearFoundFaces();
    resetAdded();
    clearFacesToAddPerson();
  };

  const prepareAndUpdatePhoto = (base64, newMode) => {
    setPhoto(base64);
    setMode(newMode);
    goToFace();
    closeModal();
  };

  const setAndDetectPhoto = (base64) => {
    cleanData();
    prepareAndUpdatePhoto(base64, FACE_PAGE_FLOWS.LOADING);
  };

  const handleUseLastPhoto = (base64) => {
    prepareAndUpdatePhoto(base64, FACE_PAGE_FLOWS.EDIT);
  };

  const handleCloseModal = () => {
    if (mode !== FACE_PAGE_FLOWS.FOUND_EVERYONE) clearRelationToCreate();
    closeModal();
  };

  const handleWebcamClick = () => setIsWebcamVisible(true);

  const handleFileStackSuccess = ({ filesUploaded: files }) => {
    const file = R.head(files);
    if (file) {
      trackEventHeadUploadSuccess("Facebook", currentUser);
      allToBase64(file.url)
        .then(rotateAndScale)
        .then(setAndDetectPhoto);
    }
  };

  const handleBrowsePhoto = ({ target: { files } }) => {
    trackEventHeadUploadSuccess("Device", currentUser);
    rotateAndScale(R.head(files)).then(setAndDetectPhoto);
  };

  const handleTakePhoto = ({ target: { files } }) => {
    trackEventHeadUploadSuccess("Device", currentUser);
    rotateAndScale(R.head(files)).then(setAndDetectPhoto);
  };

  const handleWebcamCapture = (url) => {
    trackEventHeadUploadSuccess("Webcam", currentUser);
    setAndDetectPhoto(url);
    setIsWebcamVisible(false);
  };

  const renderTitle = () => {
    if (mode === FACE_PAGE_FLOWS.FOUND_EVERYONE) {
      return (
        <DialogTitle disableTypography classes={{ root: classes.title }}>
          Add Another Face
        </DialogTitle>
      );
    }
    return (
      <DialogTitle disableTypography classes={{ root: classes.title }}>
        {R.isEmpty(relationToCreate)
          ? "Start with a Face Photo!"
          : `Upload a photo of ${relationToPronoun(relationToCreate)}`}
      </DialogTitle>
    );
  };

  const renderModalContent = () => (
    <>
      {renderTitle()}
      <List>
        {photo && (
          <ListItem classes={{ root: classes.listItem }} divider button onClick={() => handleUseLastPhoto(photo)}>
            <ListItemText disableTypography classes={{ root: classes.listItemText }}>
              Use Last Photo
            </ListItemText>
            <ListItemAvatar classes={{ root: classes.listItemAvatar }}>
              <div className={classes.lastPhoto}>
                <img src={photo} alt="Last" className={classes.lastPhotoImage} />
              </div>
            </ListItemAvatar>
          </ListItem>
        )}

        <label htmlFor="browse-photo-input">
          <ListItem classes={{ root: classes.listItem }} divider button>
            <ListItemText disableTypography classes={{ root: classes.listItemText }}>
              Browse Photos
            </ListItemText>
            <ListItemAvatar classes={{ root: classes.listItemAvatar }}>
              <img src={browsePic} alt="Browse Photos" className={classes.listItemIcon} />
            </ListItemAvatar>
          </ListItem>
          <input
            accept="image/*"
            className={classes.inputFile}
            id="browse-photo-input"
            type="file"
            onChange={handleBrowsePhoto}
          />
        </label>

        {isMobileOnly ? (
          <label htmlFor="take-photo-input">
            <ListItem classes={{ root: classes.listItem }} divider button>
              <ListItemText disableTypography classes={{ root: classes.listItemText }}>
                Take Photo
              </ListItemText>
              <ListItemAvatar classes={{ root: classes.listItemAvatar }}>
                <img src={webcamPic} alt="Webcam" className={classes.listItemIcon} />
              </ListItemAvatar>
            </ListItem>
            <input
              accept="image/*"
              className={classes.inputFile}
              id="take-photo-input"
              type="file"
              onChange={handleTakePhoto}
              capture="user"
            />
          </label>
        ) : (
          <ListItem classes={{ root: classes.listItem }} divider button onClick={handleWebcamClick}>
            <ListItemText disableTypography classes={{ root: classes.listItemText }}>
              Webcam
            </ListItemText>
            <ListItemAvatar classes={{ root: classes.listItemAvatar }}>
              <img src={webcamPic} alt="Webcam" className={classes.listItemIcon} />
            </ListItemAvatar>
          </ListItem>
        )}

        <ReactFilestack
          apikey={fileStack.appId}
          clientOptions={{ cname: fileStack.cname }}
          actionOptions={{
            fromSources: ["facebook"],
            accept: "image/*",
            disableTransformer: true,
          }}
          onSuccess={handleFileStackSuccess}
          customRender={({ onPick }) => (
            <ListItem onClick={onPick} classes={{ root: classes.listItem }} button>
              <ListItemText disableTypography classes={{ root: classes.listItemText }}>
                Facebook
              </ListItemText>
              <ListItemAvatar classes={{ root: classes.listItemAvatar }}>
                <img
                  src={facebookPic}
                  alt="Facebook"
                  className={clsx(classes.listItemIcon, classes.listItemIconFacebook)}
                />
              </ListItemAvatar>
            </ListItem>
          )}
        />
      </List>
    </>
  );

  return (
    <Dialog
      classes={{ root: classes.root }}
      PaperProps={{ classes: { root: classes.paper } }}
      open
      TransitionComponent={Transition}
    >
      <IconButton color="primary" aria-label="close" classes={{ root: classes.buttonClose }} onClick={handleCloseModal}>
        <CloseIcon fontSize="inherit" />
      </IconButton>
      {isWebcamVisible ? <Webcam onCapture={handleWebcamCapture} /> : renderModalContent()}
    </Dialog>
  );
};

export default FaceUploadModal;
